import {
    type ImageSizeFragment,
    type MonolithImageSizeFragment,
} from '@/graphql/api'
import type { ImageProps } from '@/components/Image.vue'
import type { Breakpoint } from '@/types/breakpoint.ts'

export const imageSizes = {
    small: 255,
    small_x2: 510,
    medium: 350,
    medium_x2: 700,
    large: 740,
    large_x2: 1480,
    cover: 1400,
    cover_x2: 2800,
} as const

export type ImageSizeKey = keyof typeof imageSizes
export type ImageSizeValue = (typeof imageSizes)[ImageSizeKey]

export type GenericImageSource = {
    [K in ImageSizeKey]?: MonolithImageSizeFragment | ImageSizeFragment
} & {
    url: string
    alt?: string | null
}

type ImagesByFormat = {
    format: string
    images: ImageSource[]
}

type ImageSource = {
    width: number
    src: string
}

type ImageSize = [Breakpoint, string]
export type ImageSizes = [string, ...ImageSize[]]

export function getImageProps(
    input: GenericImageSource,
    sizes?: ImageSizes
): Pick<ImageProps, 'src' | 'sources'> {
    let largest: MonolithImageSizeFragment | ImageSizeFragment | undefined

    const byFormat = Object.entries(imageSizes).reduce(
        (acc, curr) => {
            const size = curr[0] as ImageSizeKey
            const resolution = curr[1]

            const image = input[size]

            if (!image) {
                return acc
            }

            largest = image

            image.sources.forEach((src) => {
                const mime = src.mimeType

                if (!(mime in acc)) {
                    acc[mime] = {
                        format: mime,
                        images: [],
                    }
                }

                const inFormat = acc[mime]!

                inFormat.images.push({
                    src: src.url,
                    width: resolution,
                })
            })

            return acc
        },
        {} as Record<string, ImagesByFormat>
    )

    return {
        src: largest?.url || input.url,
        sources: Object.entries(byFormat).map(([mimeType, images]) => {
            const srcset = images.images
                .map((image) => {
                    return `${image.src} ${image.width}w`
                })
                .join(', ')

            return {
                mimeType,
                srcset,
                // !! DO NOT EVER DEFAULT TO AUTO
                sizes: parseSizes(sizes),
            }
        }),
    }
}

export function parseSizes(sizes?: ImageSizes): string | undefined {
    if (undefined === sizes) {
        return undefined
    }

    if (sizes.length === 0) {
        return undefined
    }

    return (
        sizes
            .map((size) => {
                if (typeof size === 'string') {
                    return size
                }

                return `(min-width: ${size[0]}px) ${size[1]}`
            })
            // Do not use sizes.toReversed(), errors in browsers
            // Also, do not mutate sizes, it will cause a mess
            .reverse()
            .join(', ')
    )
}
